import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import { useSchool } from '../contexts/SchoolContext';
import './AdminDashboard.css';
import './AdminContacts.css';

// Enquiries from the website contact form (general questions, admissions enquiries and visit requests).
// Admins and super-admins change the status (New, Contacted, Closed) through the database function, which records
// who changed it; viewers read only.
export const ENQUIRY_STATUSES = [
  { value: 'new', label: 'New', color: '#a04000' },
  { value: 'contacted', label: 'Contacted', color: '#1f618d' },
  { value: 'closed', label: 'Closed', color: '#566573' }
];
const TYPE_LABELS = { general: 'General', admissions: 'Admissions', visit_request: 'Visit request' };
const PAGE_SIZE = 25;
const COLUMNS = 'id, type, name, email, phone, child_name, grade, subject, message, status, status_changed_at, ' +
  'utm_source, utm_medium, utm_campaign, referrer, created_at';

const statusColor = (s) => ENQUIRY_STATUSES.find((x) => x.value === s)?.color ?? '#566573';
const statusLabel = (s) => ENQUIRY_STATUSES.find((x) => x.value === s)?.label ?? s;

const AdminContacts = () => {
  const { isViewer } = useAuth();
  const { gradeLabel } = useSchool();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const location = useLocation();
  // The dashboard links here with ?status=new; only known values are accepted.
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    const type = params.get('type');
    return {
      status: ENQUIRY_STATUSES.some((s) => s.value === status) ? status : '',
      type: type && Object.prototype.hasOwnProperty.call(TYPE_LABELS, type) ? type : ''
    };
  });
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState({ type: '', text: '' });

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Row-level security limits this to signed-in staff
      let query = supabase.from('contact_inquiries').select(COLUMNS, { count: 'exact' });
      if (filters.status) query = query.eq('status', filters.status);
      if (filters.type) query = query.eq('type', filters.type);
      const from = (page - 1) * PAGE_SIZE;
      const { data, count, error: queryError } = await query
        .order('created_at', { ascending: false })
        .range(from, from + PAGE_SIZE - 1);
      if (queryError) throw queryError;
      setContacts(data || []);
      setTotal(count ?? (data || []).length);
    } catch (err) {
      console.error('Failed to load enquiries', err);
      setError('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const changeFilter = (e) => {
    setFilters((f) => ({ ...f, [e.target.name]: e.target.value }));
    setPage(1);
  };

  const changeStatus = async (contact, status) => {
    if (status === contact.status) return;
    setBusyId(contact.id);
    setNotice({ type: '', text: '' });
    const { error: rpcError } = await supabase.rpc('update_enquiry_status', { p_id: contact.id, p_status: status });
    setBusyId(null);
    if (rpcError) {
      console.error('Failed to update enquiry status', rpcError);
      setNotice({
        type: 'error',
        text: rpcError.code === '42501' ? 'You do not have permission to change enquiry status' : 'Could not change the status'
      });
      return;
    }
    setContacts((list) => list.map((c) => (c.id === contact.id ? { ...c, status } : c)));
    setNotice({ type: 'success', text: `${contact.name}: marked ${statusLabel(status).toLowerCase()}` });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const replyLink = (c) =>
    `mailto:${encodeURIComponent(c.email)}?subject=${encodeURIComponent('Re: ' + (c.subject || 'Your enquiry'))}` +
    `&body=${encodeURIComponent(`Dear ${c.name},\n\nThank you for contacting us about: ${c.subject}.\n\n\n\nYour message:\n${c.message}`)}`;

  return (
    <AdminLayout>
      <div className="admin-dashboard admin-contacts">
        <div className="dashboard-header">
          <div>
            <h1>Enquiries</h1>
            <p>Questions, admissions enquiries and visit requests from the website contact form.</p>
          </div>
        </div>

        <div className="enquiry-filters">
          <label htmlFor="enquiry-status">Status</label>
          <select id="enquiry-status" name="status" value={filters.status} onChange={changeFilter}>
            <option value="">All</option>
            {ENQUIRY_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <label htmlFor="enquiry-type">Type</label>
          <select id="enquiry-type" name="type" value={filters.type} onChange={changeFilter}>
            <option value="">All</option>
            {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        {notice.text && <div className={`alert alert-${notice.type}`} role="status">{notice.text}</div>}

        {loading && (
          <div className="admin-dashboard loading">
            <div className="spinner"></div>
            <p>Loading enquiries...</p>
          </div>
        )}

        {error && (
          <div className="error-banner" role="alert">
            {error}
          </div>
        )}

        {!loading && !error && (contacts.length === 0 ? (
          <p className="no-data">{filters.status || filters.type ? 'No enquiries match these filters.' : 'No enquiries yet.'}</p>
        ) : (
          <div className="applications-table-container">
            <table className="applications-table">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Type</th>
                  <th>From</th>
                  <th>Child / grade</th>
                  <th>Subject and message</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id}>
                    <td>{new Date(c.created_at).toLocaleString()}</td>
                    <td>
                      <span className={`enquiry-type type-${c.type}`}>{TYPE_LABELS[c.type] ?? c.type}</span>
                    </td>
                    <td>
                      <div>{c.name}</div>
                      <div><a href={`mailto:${c.email}`}>{c.email}</a></div>
                      {c.phone && <div><a href={`tel:${c.phone.replace(/[^0-9+]/g, '')}`}>{c.phone}</a></div>}
                    </td>
                    <td>
                      {c.child_name && <div>{c.child_name}</div>}
                      {c.grade && <div>{gradeLabel(c.grade)}</div>}
                      {!c.child_name && !c.grade && '—'}
                    </td>
                    <td className="enquiry-message">
                      <strong>{c.subject}</strong>
                      <div title={c.message}>{c.message}</div>
                      {(c.utm_source || c.referrer) && (
                        <div className="enquiry-source">
                          Source: {[c.utm_source, c.utm_medium, c.utm_campaign].filter(Boolean).join(' / ') || c.referrer}
                        </div>
                      )}
                    </td>
                    <td>
                      {isViewer ? (
                        <span className="status-badge" style={{ background: statusColor(c.status) }}>{statusLabel(c.status)}</span>
                      ) : (
                        <select
                          aria-label={`Status of the enquiry from ${c.name}`}
                          value={c.status}
                          disabled={busyId === c.id}
                          onChange={(e) => changeStatus(c, e.target.value)}
                          style={{ borderColor: statusColor(c.status) }}
                        >
                          {ENQUIRY_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      )}
                    </td>
                    <td>
                      <a className="btn-action" title="Reply via email" href={replyLink(c)}>Reply</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="pagination">
                <button onClick={() => setPage((p) => p - 1)} disabled={page === 1}>Previous</button>
                <span>Page {page} of {totalPages}</span>
                <button onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>Next</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </AdminLayout>
  );
};

export default AdminContacts;
